import apiClient from '../apiClient';
import CourseControllers from '@/controllers/CourseControllers';

/**
 * Fetches the latest courses based on course type and count.
 * 
 * @param {number} courseType - The type of course to filter by.
 * @param {number} count - The number of courses to retrieve (default: 8).
 * @returns {Promise<Object>} The response data containing the latest courses.
 * @throws {Error} If the API request fails.
 */
export const LatestCourses = async (courseType) => {
    try {
        const response = await apiClient.get(`${CourseControllers.LatestCoursesMenu}`, {
            params: {
                courseType, // Must be passed explicitly when calling the function
                count: 8    // Fixed default value
            }
        });
        return response.data;
    } catch (error) {
        throw new Error(error.message || "Failed to fetch the latest courses");
    }
};
/**
 * Fetches a paginated list of courses with optional search term.
 * 
 * @param {Object} options - The query parameters for fetching courses.
 * @param {string} [options.SearchTerm] - The search term to filter courses.
 * @param {number} options.PageIndex - The index of the page to retrieve (1-based).
 * @param {number} options.PageSize - The number of courses per page.
 * 
 * @returns {Promise<Object>} The response data containing courses and pagination details.
 * @throws {Error} If the API request fails.
 */
export const getAllCourses = async (params) => {
    try {
        const response = await apiClient.get(`${CourseControllers.AllCoursesData}`, {
            params: params
        });
        return response;
    } catch (error) {
        throw new Error(error.message || "Failed to fetch courses");
    }
};


/**
 * Fetches a course by its unique ID.
 * 
 * @param {string} id - The unique ID of the course to fetch.
 * @returns {Promise<Object>} The response data containing the course details.
 * @throws {Error} If the API request fails.
 */
export const getCourseById = async (id) => {
    try {
        const response = await apiClient.get(`${CourseControllers.CourseById}?slug=${id}`);
        return response.data;
    } catch (error) {
        throw new Error(error.message || "Failed to fetch course by ID");
    }
};

/**
 * Similar Courses
 */
export const similarCourses = async (courseId) =>{
    try{
        const response = await apiClient.get(`${CourseControllers.SimilarCourses}`,{
            params: {courseId : courseId}
        });
        return  response.data
    }catch(error){
        throw new Error(error.message || "Failed to fetch similar courses");
    }
}


/**
 * Maps category and level values to their respective names based on the selected language.
 *
 * @param {Array} courses - The array of course objects to map.
 * @param {string} lang - The selected language ('ar' or 'en').
 * @returns {Array} The array of mapped course objects.
 */
export const mapCoursesData = (courses, lang = 'ar') => {
    if( courses.length ==undefined || courses.length ==0) return [];
    const categoryMap = {
        1: { ar: 'تطويري', en: 'Development' },
        2: { ar: 'تأهيلي', en: 'Preparatory' },
        3: { ar: 'مسجل', en: 'Registered' },
    };

    const levelMap = {
        1: { ar: 'مبتدأ', en: 'Beginner' },
        2: { ar: 'متوسط', en: 'Intermediate' },
        3: { ar: 'متقدم', en: 'Advanced' },
    };

    const methodMap = {
        1: { ar: 'حضورية', en: 'In-person' },
        2: { ar: 'مسجلة', en: 'Recorded' },
        3: { ar: 'اون لاين', en: 'Online' },
    };
    return courses.map((course) => ({
        ...course,
        categoryName: categoryMap[course.courseCategory]?.[lang] || '',
        levelName: levelMap[course.level]?.[lang] || '',
        courseMethod : methodMap[course.courseDeliveryMethod]?.[lang] || ''
    }));
};